import { useDashboardEmpresas } from '@/hooks';
import type { EmpresaDashboard } from '@/types/types';
import { BarChart3, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

function ProgBar({ value, color }: { value: number; color: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="admin-prog-bar">
        <span className="admin-prog-fill" style={{ width: `${Math.min(value, 100)}%`, background: color }} />
      </span>
      <span className="text-[12px] font-medium">{value.toFixed(0)}%</span>
    </span>
  );
}

function pColor(v: number): string {
  if (v >= 70) return 'var(--admin-green)';
  if (v >= 50) return '#e09b00';
  return 'var(--admin-red)';
}

export function AdminMatricesPage() {
  const navigate = useNavigate();
  const { empresas, isLoading, error } = useDashboardEmpresas();
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return empresas.filter((e) => !q || e.nombre?.toLowerCase().includes(q) || e.nitId?.includes(q));
  }, [empresas, search]);

  const sorted = useMemo(() => [...filtered].sort((a, b) => (b.porcentajeCumplimiento ?? 0) - (a.porcentajeCumplimiento ?? 0)), [filtered]);

  // Aggregate NTC stats
  const ntcNames = useMemo(() => {
    const map = new Map<string, { count: number; cumpl: number }>();
    empresas.forEach((e: EmpresaDashboard) => {
      const name = e.combinacionNtc?.nombre;
      if (!name) return;
      const existing = map.get(name) ?? { count: 0, cumpl: 0 };
      map.set(name, { count: existing.count + 1, cumpl: existing.cumpl + (e.porcentajeCumplimiento ?? 0) });
    });
    return Array.from(map.entries())
      .map(([name, { count, cumpl }]) => ({
        name,
        count,
        avg: count > 0 ? cumpl / count : 0,
        color: pColor(count > 0 ? cumpl / count : 0),
      }))
      .sort((a, b) => b.count - a.count);
  }, [empresas]);

  if (isLoading) {
    return (
      <div className="p-12 text-center">
        <div className="w-6 h-6 border-2 border-(--admin-blue) border-t-transparent rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (error) {
    return <div className="p-12 text-center text-(--admin-red)">{error}</div>;
  }

  return (
    <>
      {/* NTC Summary */}
      <div className="admin-card mb-6">
        <div className="admin-card-header">
          <BarChart3 className="w-4 h-4 text-(--admin-text3)" />
          <div className="text-[13px] font-semibold text-(--admin-text)">Resumen por NTC</div>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Combinación NTC</th>
              <th>Empresas</th>
              <th>Cumplimiento promedio</th>
            </tr>
          </thead>
          <tbody>
            {ntcNames.map(({ name, count, avg }) => (
              <tr key={name}>
                <td>
                  <span className="admin-chip bg-(--admin-blue-bg) text-(--admin-blue)">{name}</span>
                </td>
                <td>{count}</td>
                <td>
                  <ProgBar value={avg} color={pColor(avg)} />
                </td>
              </tr>
            ))}
            {ntcNames.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center py-6 text-(--admin-text3)">
                  Sin datos
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Empresas list for matrix navigation */}
      <div className="admin-search-row">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-(--admin-text3)" />
          <input
            type="text"
            placeholder="Buscar empresa..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="admin-search-input w-65"
          />
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <div className="text-[13px] font-semibold text-(--admin-text)">Empresas — matrices de evaluación</div>
          <span className="text-[11px] text-(--admin-text3) ml-2">{sorted.length} empresas</span>
        </div>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Empresa</th>
              <th>NIT</th>
              <th>NTC</th>
              <th>Cumplimiento</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((e) => {
              const p = e.porcentajeCumplimiento ?? 0;
              return (
                <tr key={e.nitId} className="clickable" onClick={() => navigate(`/empresas/${e.nitId}`)}>
                  <td className="font-medium text-(--admin-text)">{e.nombre}</td>
                  <td className="font-mono text-[11px] text-(--admin-text3)">{e.nitId}</td>
                  <td>
                    <span className="admin-chip bg-(--admin-blue-bg) text-(--admin-blue) text-[10px]">
                      {e.combinacionNtc?.nombre ?? '—'}
                    </span>
                  </td>
                  <td>
                    <ProgBar value={p} color={pColor(p)} />
                  </td>
                  <td>
                    <button
                      className="admin-btn text-[11px] py-1 px-2.5"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        navigate(`/empresas/${e.nitId}`);
                      }}
                    >
                      Ver →
                    </button>
                  </td>
                </tr>
              );
            })}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-8 text-(--admin-text3)">
                  Sin resultados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
